import {Edit2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {CodeText} from '@/components/shared/CodeText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {Icon} from '@/components/shared/Icon'
import type {SapAbapOutboundConfig} from '@/types/outbound-connector'

interface SapAbapOutboundConfigCardProps {
    config: SapAbapOutboundConfig
    canUpdate?: boolean
    onEdit?: () => void
}

function actionBadge(label: string, enabled: boolean) {
    return <Badge variant={enabled ? BadgeVariant.SUCCESS : 'outline'} key={label}>{label}: {enabled ? '✓' : '—'}</Badge>
}

export function SapAbapOutboundConfigCard({config, canUpdate, onEdit}: SapAbapOutboundConfigCardProps) {
    const i18n = useI18n()
    const createWithoutGrant = !config.createEnabled && config.grantEnabled
    const disableWithoutDelete = !config.disableEnabled && config.deleteEnabled
    return (
        <Card data-testid="sap-abap-config-section" data-help-prefix="sapAbapOutboundConfigCard">
            <RowCardHeader>
                <div>
                    <CardTitle data-testid="card-title-sap-abap-config" data-help-key="_section">
                        {i18n.connectors.sapAbap.connectionSection}
                    </CardTitle>
                    <CardDescription>{i18n.connectors.sapAbap.connectionDescription}</CardDescription>
                </div>
                {canUpdate && onEdit && (
                    <Button data-testid="btn-edit-sap-abap-config" size="sm" variant="outline" onClick={onEdit}>
                        <Icon icon={Edit2} className="mr-2"/>
                        {i18n.common.edit}
                    </Button>
                )}
            </RowCardHeader>
            <CardContent>
                <Stack gap="md">
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.connectors.sapAbap.odataBaseUrl}
                                     value={<CodeText>{config.odataBaseUrl}</CodeText>} fullWidth/>
                        <DetailField label={i18n.connectors.sapAbap.rfcBaseUrl}
                                     value={<CodeText>{config.rfcBaseUrl || '—'}</CodeText>} fullWidth/>
                        <DetailField label={i18n.connectors.sapAbap.mandant} value={<CodeText>{config.mandant}</CodeText>}/>
                        <DetailField label={i18n.connectors.sapAbap.userTypeDefault}
                                     value={<Badge variant="outline">{config.userTypeDefault}</Badge>}/>
                        <DetailField label={i18n.connectors.sapAbap.roleAssignmentMode}
                                     value={<Badge variant="outline">{config.roleAssignmentMode}</Badge>}/>
                        <DetailField label={i18n.connectors.sapAbap.compositeRoleExpansion}
                                     value={<Badge variant="outline">{config.compositeRoleExpansion}</Badge>}/>
                        <DetailField label={i18n.connectors.sapAbap.bnameTemplate}
                                     value={<CodeText>{config.bnameTemplate}</CodeText>}/>
                        <DetailField label={i18n.connectors.sapAbap.passwordPolicy}
                                     value={<Badge variant="outline">{config.passwordPolicy}</Badge>}/>
                        <DetailField label={i18n.connectors.sapAbap.lockReason} value={config.lockReason} fullWidth/>
                        <DetailField label={i18n.connectors.sapAbap.etagRetryLimit} value={String(config.etagRetryLimit)}/>
                    </DetailGrid>
                    <DetailField
                        label={i18n.connectors.sapAbap.allowedActions}
                        value={
                            <Stack gap="xs">
                                {actionBadge(i18n.connectors.sapAbap.actionCreate, config.createEnabled)}
                                {actionBadge(i18n.connectors.sapAbap.actionUpdate, config.updateEnabled)}
                                {actionBadge(i18n.connectors.sapAbap.actionDelete, config.deleteEnabled)}
                                {actionBadge(i18n.connectors.sapAbap.actionEnable, config.enableEnabled)}
                                {actionBadge(i18n.connectors.sapAbap.actionDisable, config.disableEnabled)}
                                {actionBadge(i18n.connectors.sapAbap.actionGrant, config.grantEnabled)}
                                {actionBadge(i18n.connectors.sapAbap.actionRevoke, config.revokeEnabled)}
                            </Stack>
                        }
                        fullWidth
                    />
                    {createWithoutGrant && (
                        <InfoBanner variant="warning">
                            {i18n.connectors.sapAbap.warnCreateWithoutGrant}
                        </InfoBanner>
                    )}
                    {disableWithoutDelete && (
                        <InfoBanner variant="warning">
                            {i18n.connectors.sapAbap.warnDisableWithoutDelete}
                        </InfoBanner>
                    )}
                    {config.bapiAllowlist && config.bapiAllowlist.trim().length > 0 && (
                        <DetailField
                            label={i18n.connectors.sapAbap.bapiAllowlist}
                            value={<CodeText>{config.bapiAllowlist}</CodeText>}
                            fullWidth
                        />
                    )}
                </Stack>
            </CardContent>
        </Card>
    )
}
